import React, { useCallback } from "react";

import { useAppContext } from "../../../../hooks/useAppContext";
import { useBottomMenu } from "../../../../hooks/useBottomMenu";
import { useLabels } from "../../../../hooks/useLabels";
import { useSelectedZone } from "../../../../hooks/useSelection";
import { IBottomMenuButton } from "../../../bottom-menu/buttons";

import { isDeleteSymbolZoneSupported } from "../menu-info";

import { useHasBottomRootMenu } from "../../../../hooks/useHasBottomRootMenu";
import { useIsProductAdvancedEditMode } from "../../../../hooks/useIsProductAdvancedEditMode";

import { BottomMenuIds, BottomMenuPaths, SYMBOLS_SUB_MENU_ID } from "../../../bottom-menu/paths";
import { SymbolsRootMenu } from "./symbols-root-menu";
import { useDashboardContext } from "../../../../contexts/dashboard-context";
import { labels } from "../labels";

export const SymbolsRootMenuContainer = () => {
	const { gotoPath, stack, currentPath } = useBottomMenu();
	const hasRootMenu = useHasBottomRootMenu();
	const dashboard = useDashboardContext();
	const {
		editor: { selection },
	} = useAppContext();
	const isAdvancedEditModeEnabled = useIsProductAdvancedEditMode();
	const zone = useSelectedZone();
	const symbolZone = eXpress.core.isSymbolZone(zone) ? zone : undefined;

	const [[symbolsTitle, deleteTitle, positionTitle], isLoadingLabels] = useLabels([
		labels.symbols,
		...(isAdvancedEditModeEnabled ? [labels.delete, labels.position] : []),
	]);

	const currentMenuItem = stack.find((menuItem) => menuItem.id === currentPath?.split("/").last())
		?.id;

	const onMenuClick = useCallback(
		(menuId: SYMBOLS_SUB_MENU_ID) => {
			if (menuId === currentMenuItem) {
				gotoPath(BottomMenuPaths.Symbols);
				return;
			}
			switch (menuId) {
				case "symbols-list":
					gotoPath(BottomMenuPaths.SymbolsList);
					return;
				case "zone-depth":
					gotoPath(BottomMenuPaths.SymbolsZoneDepth);
					return;
				default:
					gotoPath(BottomMenuPaths.Symbols);
			}
		},
		[currentMenuItem, gotoPath],
	);

	const menuItems: IBottomMenuButton<SYMBOLS_SUB_MENU_ID | "back">[] = [
		// Symbol list is always visible
		{
			id: BottomMenuIds.SymbolsList,
			iconName: "symbols",
			title: symbolsTitle,
			onClick: () => {
				onMenuClick("symbols-list");
			},
		},
	];

	function onBack() {
		selection.setCurrentZoneAndBroadcast(undefined);
		// Normally changing the selection will also update the root menu,
		// but when there is already no zone selected, it should also goto the root
		gotoPath(BottomMenuPaths.Root);
	}

	// Position
	if (isAdvancedEditModeEnabled) {
		menuItems.push({
			id: BottomMenuIds.ZoneDepth,
			iconName: "bring-to-front",
			title: positionTitle,
			isDisabled: !symbolZone,
			onClick: () => {
				onMenuClick(BottomMenuIds.ZoneDepth);
			},
		});
	}

	// Delete symbolzone button
	if (isAdvancedEditModeEnabled) {
		menuItems.push({
			id: BottomMenuIds.SymbolsZoneDelete,
			iconName: "bin",
			title: deleteTitle,
			isDisabled: !isDeleteSymbolZoneSupported(symbolZone, dashboard),
			onClick: () => {
				if (symbolZone) dashboard.removeZone(symbolZone);
			},
		});
	}

	if (isLoadingLabels || !menuItems.length) return null;

	return (
		<SymbolsRootMenu
			showBackButton={hasRootMenu}
			onBack={onBack}
			menuItems={menuItems}
			activeButtonId={currentMenuItem}
		/>
	);
};
